import { DEMO_ENTITY } from '../catalogs/banks.js';
import { z } from 'zod';
import { BusinessDateSchema } from '../common/clock.js';
import { MoneySchema } from '../common/money.js';
import { RateSchema } from '../common/rate.js';
import { CreditProductStatusSchema } from './states.js';
/**
 * Configuración del producto de crédito (`CreditProduct`), `docs/01-producto/05-core-credito.md` §2.
 * Versionado: cada cambio crea una nueva versión; líneas y utilizaciones guardan `productVersion`.
 * Valores de ejemplo = placeholders del demo hasta que la entidad los confirme.
 */
export const UsuryModalitySchema = z
    .enum([
    'MICROCREDITO',
    'CONSUMO_Y_ORDINARIO',
    'CONSUMO_BAJO_MONTO',
    'MICROCREDITO_POPULAR_PRODUCTIVO',
])
    .meta({ id: 'UsuryModality' });
export const UsuryRateSchema = z
    .object({
    modality: UsuryModalitySchema,
    validFrom: BusinessDateSchema,
    validTo: BusinessDateSchema,
    rateEA: RateSchema,
})
    .meta({ id: 'UsuryRate' });
export const MipymeFeeConfigSchema = z.object({
    basis: z.enum(['LINE_APPROVAL', 'DRAWDOWN']),
    rate: RateSchema,
    billing: z.enum(['FINANCED', 'NEXT_STATEMENT']),
    /** Tope legal configurable (Ley 590/2000, Consejo Superior de Microempresa). */
    cap: RateSchema.optional(),
});
export const LifeInsuranceConfigSchema = z.object({
    monthlyRate: RateSchema,
    basis: z.enum(['OUTSTANDING_PRINCIPAL']),
});
export const BillingConfigSchema = z.object({
    cutDay: z.int().min(1).max(28),
    dueDays: z.int().min(1).max(30),
    minDaysToFirstCut: z.int().min(0).max(30),
});
export const PaymentWaterfallComponentSchema = z.enum([
    'MORATORIUM',
    'INTEREST',
    'INSURANCE',
    'FEE',
    'PRINCIPAL',
    'PREPAYMENT',
    'CREDIT_BALANCE',
]);
export const PrepaymentModeSchema = z
    .enum(['REDUCE_TERM', 'REDUCE_INSTALLMENT'])
    .meta({ id: 'PrepaymentMode' });
/** Política de reversión: plazo para reclamar y qué se devuelve. */
export const ReversalPolicySchema = z
    .object({
    refundInterest: z.boolean(),
    refundFees: z.enum(['NONE', 'PROPORTIONAL', 'FULL']),
    refundInsurance: z.boolean().default(true),
    claimWindowDays: z.int().min(1).max(365),
    creditBalanceTreatment: z
        .enum(['KEEP_AS_CREDIT_BALANCE', 'RETURN_TO_DEPOSIT_ACCOUNT'])
        .default('KEEP_AS_CREDIT_BALANCE'),
})
    .meta({ id: 'ReversalPolicy' });
/** Efectos de una compra en disputa (`IN_DISPUTE`). */
export const DisputePolicySchema = z
    .object({
    freezeAccrual: z.boolean(),
    excludeFromMinimum: z.boolean(),
    suspendDpd: z.boolean(),
    maxDays: z.int().min(1).max(180),
    /** Días hábiles para decidir (due_at de la tarea humana). */
    decisionDueBusinessDays: z.int().min(1).max(60).default(15),
})
    .meta({ id: 'DisputePolicy' });
/** Calificación por rango de dpd: `{ A: [0,30], …, E: [121,null] }`. */
export const RatingMapSchema = z.record(z.enum(['A', 'B', 'C', 'D', 'E']), z.tuple([z.int().min(0), z.int().min(0).nullable()]));
export const CreditProductConfigSchema = z
    .object({
    code: z.string().regex(/^[A-Z0-9_]{2,40}$/),
    name: z.string().min(1),
    version: z.int().min(1),
    status: CreditProductStatusSchema,
    limitMin: MoneySchema,
    limitMax: MoneySchema,
    purchaseMin: MoneySchema,
    /** `null` = hasta el cupo disponible. */
    purchaseMax: MoneySchema.nullable(),
    rateEA: RateSchema,
    usuryModality: UsuryModalitySchema,
    moratoriumRateEA: z.union([RateSchema, z.literal('USURY')]),
    allowedInstallments: z.array(z.int().min(1).max(120)).min(1),
    mipymeFee: MipymeFeeConfigSchema,
    lifeInsurance: LifeInsuranceConfigSchema,
    billing: BillingConfigSchema,
    graceDays: z.int().min(0),
    blockPurchasesAtDpd: z.int().min(1),
    paymentWaterfall: z.array(PaymentWaterfallComponentSchema).min(1),
    prepaymentDefault: PrepaymentModeSchema,
    reversalPolicy: ReversalPolicySchema,
    disputePolicy: DisputePolicySchema,
    authorizationTtlMinutes: z
        .int()
        .min(1)
        .max(24 * 60),
    rounding: z.object({ installment: z.enum(['PESO_HALF_UP']), lastAbsorbs: z.boolean() }),
    ratingMap: RatingMapSchema,
    effectiveFrom: BusinessDateSchema.optional(),
})
    .meta({ id: 'CreditProductConfig' });
/** Valores placeholder del demo (`05-core-credito.md` §2, `03-demo/02-datos-semilla.md` §2). */
export const DEMO_CREDIT_PRODUCT = {
    code: 'MICRO_ROTATIVO',
    name: `Microcrédito Rotativo ${DEMO_ENTITY.name} (demo)`,
    version: 1,
    status: 'ACTIVE',
    limitMin: { amount: '500000.00', currency: 'COP' },
    limitMax: { amount: '30000000.00', currency: 'COP' },
    purchaseMin: { amount: '10000.00', currency: 'COP' },
    purchaseMax: null,
    rateEA: '0.450000',
    usuryModality: 'MICROCREDITO',
    moratoriumRateEA: 'USURY',
    // De 1 a 36 cuotas, todas (sin saltos): el cliente elige el plazo exacto.
    allowedInstallments: Array.from({ length: 36 }, (_, i) => i + 1),
    mipymeFee: { basis: 'DRAWDOWN', rate: '0.000000', billing: 'FINANCED' },
    lifeInsurance: { monthlyRate: '0.000500', basis: 'OUTSTANDING_PRINCIPAL' },
    billing: { cutDay: 5, dueDays: 15, minDaysToFirstCut: 10 },
    graceDays: 0,
    blockPurchasesAtDpd: 1,
    paymentWaterfall: [
        'MORATORIUM',
        'INTEREST',
        'INSURANCE',
        'FEE',
        'PRINCIPAL',
        'PREPAYMENT',
        'CREDIT_BALANCE',
    ],
    prepaymentDefault: 'REDUCE_TERM',
    reversalPolicy: {
        refundInterest: true,
        refundFees: 'PROPORTIONAL',
        refundInsurance: true,
        claimWindowDays: 60,
        creditBalanceTreatment: 'KEEP_AS_CREDIT_BALANCE',
    },
    disputePolicy: {
        freezeAccrual: true,
        excludeFromMinimum: true,
        suspendDpd: true,
        maxDays: 45,
        decisionDueBusinessDays: 15,
    },
    authorizationTtlMinutes: 15,
    rounding: { installment: 'PESO_HALF_UP', lastAbsorbs: true },
    ratingMap: { A: [0, 30], B: [31, 60], C: [61, 90], D: [91, 120], E: [121, null] },
};
//# sourceMappingURL=product.js.map